import FingerprintJS from '@fingerprintjs/fingerprintjs-pro';
import { JaneDM } from '@iheartjane/dm-sdk';

import { config } from '@jane/shared/config';
import { Storage, debugLog } from '@jane/shared/util';

export const NANOID_LENGTH = 21;

export const jdidIsNano = (jdid: string | null | undefined): boolean => {
  return jdid?.length === NANOID_LENGTH;
};

export function sendAnalytics(data: {
  fallbackId?: string;
  fpId?: string;
  jdid: string;
  juid?: string | number;
  source: string;
}) {
  if (!config.idAnalyticsUrl) return;

  // Validate required data
  if (!data?.source) {
    return;
  }

  // Check if we've already sent analytics for this source in the last 15 minutes
  const dedupeKey = `analytics_sent_${data.source}`;
  if (getLocalStorageWithTTL(dedupeKey)) {
    // Already sent analytics for this source within the TTL period
    return;
  }

  fetch(config.idAnalyticsUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  }).catch(() => {
    return;
  });

  // Record that we sent analytics for this source with a 15-minute TTL
  setLocalStorageWithTTLInMinutes(dedupeKey, 'sent', 15);
}

function setLocalStorageWithTTLInDays(
  key: string,
  value: string,
  ttlInDays: number
) {
  if (!key || !value) {
    debugLog('Trying to set an empty value!');
    return;
  }
  const expiry = Date.now() + ttlInDays * 24 * 60 * 60 * 1000;
  Storage.set(key, JSON.stringify({ value, expiry }));
}

function setLocalStorageWithTTLInMinutes(
  key: string,
  value: string,
  ttlInMinutes: number
) {
  if (!key || !value) {
    debugLog('Trying to set an empty value!');
    return;
  }
  const expiry = Date.now() + ttlInMinutes * 60 * 1000;
  Storage.set(key, JSON.stringify({ value, expiry }));
}

export function getLocalStorageWithTTL(key: string) {
  const itemStr = Storage.get(key);
  if (!itemStr) return null;
  try {
    const { value, expiry } = JSON.parse(itemStr);
    return Date.now() > expiry ? null : value;
  } catch {
    Storage.remove(key);
    return null;
  }
}

export function getLocalStorageIgnoreTTL(key: string) {
  const itemStr = Storage.get(key);
  if (!itemStr) return null;
  try {
    return JSON.parse(itemStr).value;
  } catch {
    Storage.remove(key);
    return null;
  }
}

export const getFingerprintId = async (linkedId?: string | undefined) => {
  const legacyFpKey = 'j_eyeD';
  const newFpKey = 'cachedJdid';
  const timeout = config.fingerprintTimeoutMs ?? 10000;
  try {
    const fp = await FingerprintJS.load({
      apiKey: config.fingerprintApiKey,
      endpoint: [config.fingerprintEndpoint, FingerprintJS.defaultEndpoint],
      scriptUrlPattern: [
        `${config.fingerprintScriptPath}?apiKey=<apiKey>&version=<version>&loaderVersion=<loaderVersion>`,
        FingerprintJS.defaultScriptUrlPattern,
      ],
      storageKey: 'eyeD',
    });
    const result = await fp.get({ linkedId, timeout });
    if (result?.visitorId) {
      setLocalStorageWithTTLInDays(legacyFpKey, result.visitorId, 1);
      setLocalStorageWithTTLInDays(newFpKey, result.visitorId, 1);
      return result.visitorId;
    }
    return null;
  } catch (error) {
    console.debug('FP Fetch Error: ', error);
    return null;
  }
};

export const generateFallbackJdid = () => {
  return JaneDM.generateJaneDeviceId();
};

export const useGetJaneDeviceId = () => {
  // TODO: change this to not return as { data: string }
  const jdidKey = 'jdid';
  const availableId = Storage.get('jdid');
  if (availableId) return { data: availableId };
  const cookie = getCookie('jdid');
  if (cookie) {
    Storage.set(jdidKey, cookie);
    return { data: cookie };
  }
  debugLog(
    'JDID not available was not available in useGetJaneDeviceId. No reason for alarm.'
  );
  const newId = generateFallbackJdid();
  Storage.set(jdidKey, newId);
  return { data: newId };
};

export const getCookie = (name: string): string | null => {
  const cookieValue = document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${name}=`));
  return cookieValue ? decodeURIComponent(cookieValue.split('=')[1]) : null;
};
